import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CollaboratorCombobox, ClientCombobox, PositionCombobox, SeniorityCombobox } from '@/components/shared';
import { usePositions } from '@/hooks/usePositions';
import { useSeniorities } from '@/hooks/useSeniorities';
import { Alert } from '@/components/Alert';
import { useCollaboratorByIdQuery, type CollaboratorPublic } from '@/features/Collaborators/Data';
import { useCollaboratorAllocationsQuery } from '@/features/Collaborators/Allocations/data';
import { AllocationStatuses } from '@/features/Collaborators/Models';
import { useAuth, ROLE_SI_ADMIN, ROLE_SI_RH } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/useToast';
import {
  useSolicitacaoMutations,
  useRegrasClienteQuery,
  useEquipamentosDoColaboradorQuery,
  useCamposAberturaEmailCliente,
} from '../../hooks/data';
import DadosClienteAbertura from './components/DadosClienteAbertura';
import {
  TIPO_SOLICITACAO_OPCOES,
  TIPOS_SOLICITACAO_COM_DATA_OBRIGATORIA,
  TIPO_FLUXO_PADRAO_POR_SOLICITACAO,
  TIPOS_SOLICITACAO_COM_DEVOLUCAO_AUTOMATICA,
  TIPOS_SOLICITACAO_COM_SUGESTAO_DEVOLUCAO_UNICA,
  TIPOS_SOLICITACAO_COM_SUGESTAO_DEVOLUCAO_TOTAL,
  TIPOS_SOLICITACAO_SEM_MOVIMENTACAO,
} from './novaSolicitacaoPage.definitions';
import {
  TIPO_EQUIPAMENTO_LABELS,
  PROPRIEDADE_EQUIPAMENTO_OPCOES,
  labelPropriedadeEquipamento,
  SISTEMA_OPERACIONAL_PRINCIPAL_LABELS,
  TIPO_EQUIPAMENTO_POR_SISTEMA_OPERACIONAL_PRINCIPAL,
  TIPOS_EQUIPAMENTO_SEM_SO,
  SISTEMAS_ACESSO_CRIACAO,
  SISTEMAS_ACESSO_RESET_SENHA,
  ACAO_ACESSO_SOLICITACAO_LABELS,
} from '../SolicitacaoDetailsPage/solicitacaoDetailsPage.definitions';
import { dataInputParaDate } from '../../utils/dataInput';
import type { SistemaOperacionalPrincipal } from '../SolicitacaoDetailsPage/solicitacaoDetailsPage.definitions';
import type {
  TipoSolicitacao,
  TipoEquipamentoMovimentacao,
  PropriedadeEquipamento,
  AcaoAcesso,
} from '../../models';
import ListaEquipamentosColaborador from './components/ListaEquipamentosColaborador';
import {
  itemOutroEquipamentoDevolucao as montarItemOutroEquipamento,
  itensIntakePorEquipamentos,
  sugestaoSoParaTroca,
} from '../../utils/equipamentosDevolucao';
import type { NovaSolicitacaoContentProps } from './novaSolicitacaoContent.types';
import { normalizarPatrimonio } from '../../models/equipamento.utils';
import { tipoSolicitacaoExigeMotivo, validarMotivoSolicitacao } from '../../models';
import { comentarioSolicitacaoService } from '../../services';
import MotivoSolicitacaoCampos from '../../components/MotivoSolicitacaoCampos';
import TipoEquipamentoPedido, { TIPOS_PEDIDO_COM_DESCRICAO } from './components/TipoEquipamentoPedido';

/** Opção "Outro cliente (alocação ainda não cadastrada)" do seletor de alocação — só Nova Alocação. */
const ALOCACAO_OUTRO_CLIENTE = '__outro_cliente__';

/**
 * Criação de uma nova Solicitação do módulo SI — inclui, no mesmo formulário, os dados
 * da primeira Movimentação (tipo de equipamento, SO, homologação, propriedade, data).
 *
 * Quem sabe o quê (decisão de produto): tipo de equipamento/SO/homologado/data são
 * informados por quem abre o pedido (varia por projeto/stack do colaborador) — o SI só
 * entra depois com os dados de execução (patrimônio, nº série, logística), na tela de
 * detalhes. Por isso a Movimentação nasce junto da Solicitação num único envio, em vez
 * de exigir uma segunda tela.
 *
 * Admissão é um caso especial: a pessoa ainda não existe como colaborador no Radar (sem
 * conta Microsoft ainda), então não há o que buscar no CollaboratorCombobox — captura-se
 * nome/e-mail em texto livre (`colaboradorPendente`), sem `colaboradorId` (fica null até
 * a ação "Vincular Colaborador", ver SolicitacaoDetailsPage). Não faz sentido "para mim"
 * nesse caso (decisão de produto).
 *
 * `onCriada`/`onCancelar` em vez de navegação direta: este conteúdo é reaproveitado tanto
 * pela rota `/si/nova` (NovaSolicitacaoPage, que navega) quanto pelo drawer aberto a partir
 * da lista (NovaSolicitacaoDrawer, que fecha o painel) — mesmo padrão de extração já usado
 * em SolicitacaoDetailsContent/SolicitacaoDetailsPage/SolicitacaoDrawer.
 */
export default function NovaSolicitacaoContent({
  className,
  onCriada,
  onCancelar,
  valoresIniciais,
}: NovaSolicitacaoContentProps) {
  const { user, userHasRole } = useAuth();
  const toast = useToast();
  const { criarSolicitacaoComMovimentacao } = useSolicitacaoMutations();

  const isSI = userHasRole(ROLE_SI_ADMIN);
  const { data: regrasCliente = [] } = useRegrasClienteQuery();

  // Prefill vindo da tela de Alocações (registrar/encerrar alocação): por URL quando é o
  // deep-link direto `/si/nova?...`, ou por props (`valoresIniciais`) quando este conteúdo
  // é aberto como drawer sem navegar (ver NovaSolicitacaoDrawer) — nesse caso não há query
  // string pra ler. Módulo Collaborators não importa nada do SI além do próprio drawer, pra
  // manter os 2 módulos desacoplados. Só usado pra montar o estado inicial (lido 1x, com
  // useState(() => ...)) — depois disso é um formulário normal, totalmente editável.
  const [searchParams] = useSearchParams();
  const tipoSolicitacaoParam = valoresIniciais?.tipoSolicitacao ?? searchParams.get('tipoSolicitacao');
  const colaboradorIdParam = valoresIniciais?.colaboradorId ?? searchParams.get('colaboradorId');
  const clienteIdParam = valoresIniciais?.clienteId ?? searchParams.get('clienteId');
  const clienteNomeParam = valoresIniciais?.clienteNome ?? searchParams.get('clienteNome');
  const alocacaoReferenciadaIdParam =
    valoresIniciais?.alocacaoReferenciadaId ?? searchParams.get('alocacaoReferenciadaId');
  const clienteDevolucaoIdParam = valoresIniciais?.clienteDevolucaoId ?? searchParams.get('clienteDevolucaoId');
  const clienteDevolucaoNomeParam =
    valoresIniciais?.clienteDevolucaoNome ?? searchParams.get('clienteDevolucaoNome');

  const [paraMim, setParaMim] = useState(() => !colaboradorIdParam || colaboradorIdParam === user?.collaboratorId);
  // Sem valor padrão de propósito (decisão registrada) — um tipo pré-selecionado já
  // causou solicitação aberta com o tipo errado (quem abre nem percebe que precisa trocar).
  // Só via deep-link (`tipoSolicitacaoParam`, ex. vindo do wizard de Alocações) começa
  // preenchido; aberto direto pelo menu, força escolha explícita (ver `camposFaltando`).
  const [tipoSolicitacao, setTipoSolicitacao] = useState<TipoSolicitacao | ''>(() => {
    const valido = TIPO_SOLICITACAO_OPCOES.some((opcao) => opcao.value === tipoSolicitacaoParam);
    return valido ? (tipoSolicitacaoParam as TipoSolicitacao) : '';
  });
  const [colaboradorSelecionado, setColaboradorSelecionado] = useState<CollaboratorPublic | null>(null);
  const [nomePendente, setNomePendente] = useState('');
  const [emailPendente, setEmailPendente] = useState('');
  // Cargo/Senioridade não vêm do candidato (não tem relação com pré-cadastro) — quem sabe é
  // o RH que abre a Admissão. Mesmos dropdowns/dados do perfil do Colaborador (Dados de
  // Contratação), pra ficar igual e dar pra importar direto na "Vincular Colaborador"
  // (ver onSolicitacaoWrite nas Functions), sem o RH preencher de novo.
  const [positionIdPendente, setPositionIdPendente] = useState('');
  const [seniorityIdPendente, setSeniorityIdPendente] = useState('');
  const { positions, loading: positionsLoading, error: positionsError } = usePositions();
  const { seniorities, loading: senioritiesLoading, error: senioritiesError } = useSeniorities();
  const [cliente, setCliente] = useState<{ id: string; name: string } | null>(
    () => (clienteIdParam ? { id: clienteIdParam, name: clienteNomeParam ?? '' } : null)
  );
  const [observacoes, setObservacoes] = useState('');
  const [dadosCliente, setDadosCliente] = useState<Record<string, string>>({});
  const [motivo, setMotivo] = useState('');
  const [motivoDetalhe, setMotivoDetalhe] = useState('');
  // Cada tipo tem sua lista de motivos — trocar o tipo zera a escolha.
  useEffect(() => {
    setMotivo('');
  }, [tipoSolicitacao]);

  // Admissão/Desligamento só podem ser abertos por quem tem si_rh — nem si_admin sozinho
  // abre (decisão de produto explícita: "nem o SI pode abrir chamado, só o RH mesmo"). Os
  // demais tipos continuam abertos a qualquer colaborador (ver também firestore.rules, que
  // reforça isso no servidor: esconder aqui é só UX, não é a barreira de segurança real).
  const podeAbrirAdmissaoDesligamento = userHasRole(ROLE_SI_RH);
  // "Formatação" só nasce pelo wizard de Alocações (Mudança de Projeto) — ele já sabe o
  // Cliente pelo registro da alocação, então não faz sentido escolher esse tipo na mão
  // (ninguém saberia preencher o Cliente certo, ver secaoEquipamentoEnvio abaixo). Mas
  // precisa continuar aparecendo na lista QUANDO já é o tipo atual (vindo do wizard) —
  // senão o <select> do HTML perde a seleção (value sem <option> correspondente).
  const opcoesTipoSolicitacao = TIPO_SOLICITACAO_OPCOES.filter((opcao) => {
    if (opcao.value === 'formatacao') return tipoSolicitacao === 'formatacao';
    // Divergência de inventário tem formulário próprio (Meus equipamentos, Portal do SI).
    if (opcao.value === 'divergencia_inventario') return tipoSolicitacao === 'divergencia_inventario';
    if (!podeAbrirAdmissaoDesligamento && (opcao.value === 'admissao' || opcao.value === 'desligamento')) {
      return false;
    }
    return true;
  });

  // Pergunta invertida (decisão de produto): quem abre a solicitação sabe o Sistema
  // Operacional que precisa, não o tipo físico do equipamento — o tipo é derivado da
  // resposta (ver solicitacaoDetailsPage.definitions.ts). "sem_so" cobre quem precisa de
  // equipamento sem SO (monitor, periférico, outro) — não é "não sei", é uma resposta
  // concreta (rejeitamos deixar em branco pro SI decidir, decisão explícita do usuário).
  // macOS não deriva um tipo exato (Macbook/Mac Mini/iMac): qual vai pra mão do
  // colaborador depende do que tem disponível em estoque, não de preferência — o SI
  // escolhe depois. Por isso, ao contrário de "sem_so", aqui o tipo NÃO é obrigatório
  // pra abrir a solicitação (só o SO precisa estar definido).
  const [soPrincipalEscolhido, setSoPrincipal] = useState<SistemaOperacionalPrincipal | 'sem_so' | ''>('');
  const [tipoSemSoEscolhido, setTipoSemSo] = useState<TipoEquipamentoMovimentacao | ''>('');
  // Troca/Mudança de Projeto: enquanto ninguém mexe no SO, ele acompanha o equipamento que
  // vai ser devolvido (sugestaoSoParaTroca). Escolheu à mão uma vez, nunca mais muda sozinho.
  const [soManual, setSoManual] = useState(false);
  const [homologado, setHomologado] = useState(false);
  const [propriedadeEquipamento, setPropriedadeEquipamento] = useState<PropriedadeEquipamento | ''>('');
  const [data, setData] = useState('');
  // Mais de um pode ser escolhido pra devolução (ex.: notebook + monitor no mesmo pedido) —
  // por isso um Set, não um id único.
  const [equipamentosDevolucaoEscolhidosIds, setEquipamentosDevolucaoEscolhidosIds] = useState<Set<string>>(
    () => new Set(valoresIniciais?.equipamentoIds ?? [])
  );
  // Só usados em Mudança de Projeto/Troca de Equipamento (geraDevolucaoAutomatica) — a
  // devolução é uma Movimentação separada do envio, com Cliente/data próprios (pode ser
  // de um projeto diferente e não acontecer na mesma data, ver Movimentacao.clienteId).
  const [clienteDevolucao, setClienteDevolucao] = useState<{ id: string; name: string } | null>(
    () => (clienteDevolucaoIdParam ? { id: clienteDevolucaoIdParam, name: clienteDevolucaoNomeParam ?? '' } : null)
  );
  const [dataRecolhimentoDevolucao, setDataRecolhimentoDevolucao] = useState('');
  // Equipamento a devolver que não está entre os já cadastrados (equipamentosColaborador)
  // — texto livre, vira um item extra além dos sugeridos; o SI completa o cadastro depois.
  const [outroEquipamentoDevolucao, setOutroEquipamentoDevolucao] = useState('');

  // Calculado antes do `if (!user) return null` abaixo — precisa vir antes de
  // useEquipamentosDoColaboradorQuery (Regras de Hooks: nunca condicionar chamada de hook
  // a um retorno antecipado). `colaboradorIdParaDevolucao` cobre os 2 jeitos de saber
  // quem é o colaborador-alvo nesse ponto do fluxo (ainda antes de `colaboradorAlvo` ser
  // montado logo abaixo).
  const geraDevolucaoAutomatica =
    tipoSolicitacao !== '' && TIPOS_SOLICITACAO_COM_DEVOLUCAO_AUTOMATICA.includes(tipoSolicitacao);
  const sugereDevolucaoUnica =
    tipoSolicitacao !== '' && TIPOS_SOLICITACAO_COM_SUGESTAO_DEVOLUCAO_UNICA.includes(tipoSolicitacao);
  const sugereDevolucaoTotal =
    tipoSolicitacao !== '' && TIPOS_SOLICITACAO_COM_SUGESTAO_DEVOLUCAO_TOTAL.includes(tipoSolicitacao);
  const buscaEquipamentoDevolucao = sugereDevolucaoUnica || sugereDevolucaoTotal;
  const colaboradorIdParaDevolucao = paraMim ? user?.collaboratorId : colaboradorSelecionado?.id;
  const { data: equipamentosColaborador = [] } = useEquipamentosDoColaboradorQuery(
    buscaEquipamentoDevolucao ? (colaboradorIdParaDevolucao ?? undefined) : undefined
  );
  // Mudança de Projeto/Troca de Equipamento/Devolução: quem abre marca qual(is) devolve (pode
  // ser mais de um) — mesmo com 1 só vinculado, que não entra sozinho: o que vai ser devolvido
  // pode ser outro (campo "Outro equipamento"). Desligamento leva todos (sugereDevolucaoTotal,
  // ver equipamentosIntake no submit).
  const equipamentosParaDevolver = equipamentosColaborador.filter((equipamento) =>
    equipamentosDevolucaoEscolhidosIds.has(equipamento.id)
  );
  const sugestaoSo = geraDevolucaoAutomatica && !soManual ? sugestaoSoParaTroca(equipamentosParaDevolver) : null;
  const soPrincipal = sugestaoSo ? sugestaoSo.soPrincipal : soPrincipalEscolhido;
  const tipoSemSo = sugestaoSo ? sugestaoSo.tipoSemSo : tipoSemSoEscolhido;

  // Alocações do colaborador-alvo (undefined pra Admissão — pessoa ainda não existe,
  // colaboradorIdParaDevolucao resolve pra undefined nesse caso) — usadas pra vincular a
  // Solicitação a um projeto específico (ver alocacaoEscolhida abaixo), não só devolução.
  // Desligamento fica de fora de propósito: afeta TODAS as alocações da pessoa (ela está
  // saindo da empresa), não uma específica — não há "qual projeto" a perguntar.
  const { data: alocacoesColaborador = [] } = useCollaboratorAllocationsQuery(colaboradorIdParaDevolucao);
  const alocacoesAtivas =
    tipoSolicitacao === 'desligamento'
      ? []
      : alocacoesColaborador.filter((alocacao) => alocacao.status === AllocationStatuses.Active);
  const [alocacaoEscolhidaId, setAlocacaoEscolhidaId] = useState(alocacaoReferenciadaIdParam ?? '');
  // Nova Alocação é justamente um projeto a mais: a única alocação ativa costuma ser a ATUAL,
  // não a nova (aberta direto pelo SI, antes de cadastrar a alocação) — sempre pergunta, com
  // a opção de um Cliente cuja alocação ainda não existe (ALOCACAO_OUTRO_CLIENTE).
  const ehNovaAlocacao = tipoSolicitacao === 'nova_demanda';
  const alocacaoEscolhida =
    alocacoesAtivas.length === 1 && !ehNovaAlocacao
      ? alocacoesAtivas[0]
      : alocacoesAtivas.find((alocacao) => alocacao.id === alocacaoEscolhidaId);
  const perguntaAlocacao = alocacoesAtivas.length > 1 || (ehNovaAlocacao && alocacoesAtivas.length > 0);
  // Só pede a escolha quando é ambíguo (2+ alocações ativas) — com 0 ou 1, não há decisão
  // real a tomar (ver alocacaoEscolhida acima, que já resolve sozinho o caso de 1 só).
  // 'acessos' nunca pede — é pedido pontual de sistema interno, não amarrado a um
  // projeto/cliente (mesmo raciocínio de `isAcessos` mais abaixo).
  const precisaEscolherAlocacao =
    tipoSolicitacao !== 'acessos' && perguntaAlocacao && !alocacaoEscolhida && alocacaoEscolhidaId !== ALOCACAO_OUTRO_CLIENTE;

  // Busca o colaborador do prefill (vindo de Alocações) só quando é outra pessoa — pra
  // preencher nome/e-mail de verdade (o ClientCombobox/CollaboratorCombobox resolvem o
  // nome sozinhos pra exibição, mas o estado aqui embaixo precisa do valor real pra
  // gravar colaboradorEmail/colaboradorNome corretos na Solicitação).
  const precisaBuscarColaboradorPrefill = Boolean(
    colaboradorIdParam && colaboradorIdParam !== user?.collaboratorId && !colaboradorSelecionado
  );
  const { data: colaboradorPrefill } = useCollaboratorByIdQuery(
    precisaBuscarColaboradorPrefill ? (colaboradorIdParam ?? undefined) : undefined
  );
  useEffect(() => {
    if (colaboradorPrefill && precisaBuscarColaboradorPrefill) {
      setColaboradorSelecionado({
        id: colaboradorPrefill.id,
        email: colaboradorPrefill.email ?? '',
        name: colaboradorPrefill.name,
      } as CollaboratorPublic);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colaboradorPrefill]);

  // Desligamento nunca é "pra mim" — ninguém abre o próprio desligamento (decisão de
  // produto). Força mesmo se `paraMim` tiver nascido `true` (ex.: sem colaboradorIdParam
  // na URL, que é o caso normal ao abrir /si/nova direto e escolher Desligamento no select).
  const isDesligamento = tipoSolicitacao === 'desligamento';
  useEffect(() => {
    if (isDesligamento && paraMim) {
      setParaMim(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDesligamento]);

  // Bloco "Dados para o cliente" — só quando o Cliente tem e-mail configurado pelo SI.
  const camposDadosCliente = useCamposAberturaEmailCliente(
    tipoSolicitacao,
    [cliente?.id, clienteDevolucao?.id],
    { colaboradorId: colaboradorIdParaDevolucao, emailPessoal: emailPendente }
  );

  if (!user) {
    return null;
  }

  const isAdmissao = tipoSolicitacao === 'admissao';
  const isAcessos = tipoSolicitacao === 'acessos';
  const naoTemMovimentacao = tipoSolicitacao !== '' && TIPOS_SOLICITACAO_SEM_MOVIMENTACAO.includes(tipoSolicitacao);

  // Cliente vem sempre da Alocação (automática, com 1 só ativa; ou a escolhida, com 2+) —
  // nunca perguntado de novo à parte: os dois vinham da mesma fonte (ver
  // CollaboratorAllocationFormPage, que já manda clienteId e alocacaoReferenciadaId juntos
  // pro wizard) e ficavam redundantes lado a lado na tela (combinado com o usuário).
  // Só continua manual (ClientCombobox visível, ver abaixo) quando não existe nenhuma
  // Alocação ativa pra derivar. Não se aplica a Acessos (não amarrado a cliente) nem aos
  // tipos com devolução automática (Troca de Equipamento/Mudança de Projeto têm Cliente
  // próprio por Movimentação — envio pode ser de um Cliente, devolução de outro).
  useEffect(() => {
    if (isAcessos || geraDevolucaoAutomatica || naoTemMovimentacao) return;
    if (!alocacaoEscolhida?.clientId) return;
    setCliente({ id: alocacaoEscolhida.clientId, name: alocacaoEscolhida.clientName ?? '' });
  }, [alocacaoEscolhida, isAcessos, geraDevolucaoAutomatica, naoTemMovimentacao]);

  // Pedido pontual de criação/reset de senha de 1+ sistemas — fora do fluxo padrão de
  // Admissão/Desligamento (ver CATALOGO_ACESSOS/SISTEMAS_ACESSO_CRIACAO). Criação nunca
  // lista Microsoft (quem acessa o Digital já tem Microsoft, é o próprio login). Mesma
  // ação pra todos os sistemas marcados — pra ações diferentes, é outra Solicitação.
  const [acaoAcesso, setAcaoAcesso] = useState<AcaoAcesso | ''>('');
  const [sistemasAcesso, setSistemasAcesso] = useState<Set<string>>(new Set());
  const sistemasAcessoDisponiveis = acaoAcesso === 'criar' ? SISTEMAS_ACESSO_CRIACAO : SISTEMAS_ACESSO_RESET_SENHA;

  const toggleSistemaAcesso = (sistema: string) => {
    setSistemasAcesso((atual) => {
      const novo = new Set(atual);
      if (novo.has(sistema)) novo.delete(sistema);
      else novo.add(sistema);
      return novo;
    });
  };

  // Desligamento: os campos de equipamento (tipo/SO/homologado/propriedade) ficam a
  // cargo do SI — quem abre a Solicitação não sabe/não precisa informar (decisão de
  // produto). A Data continua pedida normalmente (não é um "campo de equipamento").
  // Formatação não tem Movimentação nenhuma, então não faz sentido perguntar nada disso.
  // Devolução: o equipamento já é escolhido na lista (com patrimônio) — perguntar o SO de
  // novo era identificar o mesmo equipamento duas vezes (feedback do SI).
  const ehDevolucao = tipoSolicitacao === 'devolucao';
  const mostrarCamposEquipamento = tipoSolicitacao !== 'desligamento' && !ehDevolucao && !naoTemMovimentacao;

  // Periféricos e Acessórios: pergunta o tipo primeiro (TipoEquipamentoPedido) — sem
  // Windows/macOS (computador vai por Troca/Nova Demanda); só o smartphone pergunta o SO.
  const isPedidoPerifericos = tipoSolicitacao === 'pedido_colaborador';
  const [descricaoItemPedido, setDescricaoItemPedido] = useState('');
  const pedeDescricaoItem = isPedidoPerifericos && TIPOS_PEDIDO_COM_DESCRICAO.includes(tipoSemSo as TipoEquipamentoMovimentacao);
  // Trocou pra Periféricos com um computador (Windows/macOS) escolhido antes: limpa — essas
  // opções não existem aqui e ficariam escolhidas sem aparecer.
  useEffect(() => {
    if (isPedidoPerifericos && (soPrincipal === 'windows' || soPrincipal === 'macos')) {
      setSoPrincipal('');
      setTipoSemSo('');
    }
  }, [isPedidoPerifericos, soPrincipal]);
  const tipoSemSoVisivel = mostrarCamposEquipamento && soPrincipal === 'sem_so' && !isPedidoPerifericos;

  // Deriva o tipo quando dá pra saber sem ambiguidade (windows/android/ios) ou quando foi
  // escolhido direto (sem_so). Pra macOS fica vazio de propósito — ver comentário no
  // estado de `soPrincipal` acima.
  const tipoEquipamento: TipoEquipamentoMovimentacao | '' = !mostrarCamposEquipamento
    ? ''
    : soPrincipal === 'sem_so'
      ? tipoSemSo
      : soPrincipal
        ? TIPO_EQUIPAMENTO_POR_SISTEMA_OPERACIONAL_PRINCIPAL[soPrincipal] ?? ''
        : '';

  // Regras de exceção por Cliente, configuráveis pelo SI (ver ConfiguracoesDrawer) — os
  // clientes fora dessas listas nem mostram os campos correspondentes.
  const regraCliente = regrasCliente.find((regra) => regra.clienteId === cliente?.id);
  // "Homologado" é uma pergunta de imagem/padrão corporativo do SO (Windows ou macOS),
  // não do tipo físico exato — por isso depende do SO escolhido, não do tipo derivado
  // (que fica vazio pra macOS). Some-se a isso: só aparece pros Clientes selecionados em
  // "Equipamento Homologado" nas Configurações — os demais nunca usam máquina homologada.
  const homologadoVisivel =
    mostrarCamposEquipamento &&
    (soPrincipal === 'windows' || soPrincipal === 'macos') &&
    regraCliente?.usaEquipamentoHomologado === true;
  // Só alguns Clientes permitem usar equipamento do próprio cliente (em vez de sempre Raro).
  // Periférico e cartão de acesso são sempre da Raro — nem pergunta (vai 'raro' no payload,
  // como nos demais casos em que a pergunta não aparece).
  const propriedadeVisivel =
    mostrarCamposEquipamento &&
    regraCliente?.permiteEquipamentoCliente === true &&
    tipoEquipamento !== 'cartao_acesso' &&
    tipoEquipamento !== 'periferico';
  const dataObrigatoria = tipoSolicitacao !== '' && TIPOS_SOLICITACAO_COM_DATA_OBRIGATORIA.includes(tipoSolicitacao);
  const dataVisivel = dataObrigatoria || isSI;

  const colaboradorAlvo = paraMim
    ? { id: user.collaboratorId ?? '', email: user.email ?? '', name: user.displayName ?? user.email ?? '' }
    : colaboradorSelecionado
      ? { id: colaboradorSelecionado.id, email: colaboradorSelecionado.email, name: colaboradorSelecionado.name }
      : null;

  const dadosSolicitacaoValidos = isAdmissao
    ? Boolean(nomePendente.trim() && emailPendente.trim())
    : Boolean(colaboradorAlvo?.id && colaboradorAlvo?.email);

  // Com o "Outro equipamento" preenchido, não precisa marcar nenhum da lista — o que vai ser
  // devolvido pode não estar entre os que estão com o colaborador.
  const precisaEscolherEquipamentoDevolucao =
    sugereDevolucaoUnica &&
    equipamentosColaborador.length > 0 &&
    equipamentosParaDevolver.length === 0 &&
    !outroEquipamentoDevolucao.trim();
  // "Devolução" não cria uma 2ª Movimentação (geraDevolucaoAutomatica é falso pra ela) —
  // sua única Movimentação já é a devolução, então a sugestão vai direto nela.
  // Mudança de Projeto/Troca de Equipamento aplicam na 2ª Movimentação (ver adiante).
  const sugestaoNaMovimentacaoPrincipal = sugereDevolucaoUnica && !geraDevolucaoAutomatica;
  // Devolução sem nada marcado nem descrito: a Movimentação nasceria vazia.
  const semEquipamentoParaDevolver =
    ehDevolucao && !precisaEscolherEquipamentoDevolucao && equipamentosParaDevolver.length === 0 && !outroEquipamentoDevolucao.trim();

  // "Equipamento homologado" nunca entra aqui: é um checkbox/toggle, sempre tem um valor
  // (começa em "não"/false) — não existe estado "vazio" a validar (feedback do usuário).
  // Só o SO é exigido pra abrir a solicitação — `tipoEquipamento` fica opcional (fica
  // vazio pra macOS, o SI escolhe o modelo depois). "sem_so" é a exceção: aí o tipo exato
  // (monitor/periférico/outro) continua obrigatório, porque não tem SO pra dar nenhuma pista.
  const dadosEquipamentoValidos =
    (!mostrarCamposEquipamento || (Boolean(soPrincipal) && (!tipoSemSoVisivel || Boolean(tipoSemSo)))) &&
    (!dataObrigatoria || Boolean(data));

  const motivoPendente = tipoSolicitacao ? validarMotivoSolicitacao(tipoSolicitacao, motivo, motivoDetalhe) : null;

  const podeSubmeter = Boolean(
    tipoSolicitacao &&
      dadosSolicitacaoValidos &&
      !motivoPendente &&
      !(pedeDescricaoItem && !descricaoItemPedido.trim()) &&
      (isAcessos || cliente?.id) &&
      user.collaboratorId &&
      dadosEquipamentoValidos &&
      !precisaEscolherEquipamentoDevolucao &&
      !semEquipamentoParaDevolver &&
      !precisaEscolherAlocacao &&
      (!isAcessos || (acaoAcesso && sistemasAcesso.size > 0))
  );

  const camposFaltando: string[] = [];
  if (!tipoSolicitacao) camposFaltando.push('Tipo de Solicitação');
  if (motivoPendente) camposFaltando.push(motivoPendente);
  if (isAdmissao) {
    if (!nomePendente.trim()) camposFaltando.push('Nome');
    if (!emailPendente.trim()) camposFaltando.push('E-mail');
  } else if (!colaboradorAlvo?.id) {
    camposFaltando.push('Colaborador');
  }
  if (!isAcessos && !cliente?.id) camposFaltando.push('Cliente');
  if (isAcessos) {
    if (!acaoAcesso) camposFaltando.push('Ação');
    if (sistemasAcesso.size === 0) camposFaltando.push('Sistema');
  }
  if (precisaEscolherEquipamentoDevolucao) camposFaltando.push('Equipamento a devolver');
  else if (semEquipamentoParaDevolver) camposFaltando.push('Equipamento a devolver (descreva)');
  if (precisaEscolherAlocacao) camposFaltando.push('Alocação');
  if (mostrarCamposEquipamento) {
    if (!soPrincipal) camposFaltando.push(isPedidoPerifericos ? 'Tipo de Equipamento (e o sistema, se smartphone)' : 'Sistema Operacional');
    if (pedeDescricaoItem && !descricaoItemPedido.trim()) camposFaltando.push('Qual item');
    else if (tipoSemSoVisivel && !tipoSemSo) camposFaltando.push('Tipo de Equipamento');
  }
  if (dataObrigatoria && !data) camposFaltando.push('Data');

  const dadosClienteValores = camposDadosCliente
    .map((campo) => [campo.chave, dadosCliente[campo.chave]?.trim() ?? ''] as const)
    .filter(([, valor]) => valor);
  const dadosClienteParaSalvar = dadosClienteValores.length > 0 ? Object.fromEntries(dadosClienteValores) : undefined;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!podeSubmeter || !user.collaboratorId || !user.email || (!isAcessos && !cliente?.id) || !tipoSolicitacao) {
      return;
    }

    try {
      // Equipamento a devolver descrito em texto livre (não está entre os já cadastrados)
      // — item extra, além dos sugeridos; sem equipamentoSugeridoId, o SI cadastra depois.
      const itemOutroEquipamentoDevolucao = montarItemOutroEquipamento(outroEquipamentoDevolucao);

      const solicitacaoId = await criarSolicitacaoComMovimentacao.mutateAsync({
        solicitacao: isAdmissao
          ? {
              tipoSolicitacao,
              colaboradorId: null,
              colaboradorEmail: emailPendente.trim(),
              colaboradorNome: nomePendente.trim(),
              colaboradorPendente: {
                nome: nomePendente.trim(),
                emailInformado: emailPendente.trim(),
                positionId: positionIdPendente || null,
                positionName: positions.find((p) => p.id === positionIdPendente)?.name ?? null,
                seniorityId: seniorityIdPendente || null,
                seniorityName: seniorities.find((s) => s.id === seniorityIdPendente)?.name ?? null,
              },
              solicitanteId: user.collaboratorId,
              solicitanteEmail: user.email,
              clienteId: cliente?.id ?? null,
              alocacaoReferenciadaId: alocacaoReferenciadaIdParam || undefined,
              dadosCliente: dadosClienteParaSalvar,
            }
          : {
              tipoSolicitacao,
              colaboradorId: colaboradorAlvo?.id ?? null,
              colaboradorEmail: colaboradorAlvo?.email ?? '',
              colaboradorNome: colaboradorAlvo?.name ?? '',
              solicitanteId: user.collaboratorId,
              solicitanteEmail: user.email,
              // 'acessos' não amarra a um projeto/cliente (ver comentário no schema).
              clienteId: isAcessos ? null : (cliente?.id ?? null),
              // Observações viram a 1ª postagem (comentarios), logo após criar — ver abaixo.
              motivoSolicitacao: tipoSolicitacaoExigeMotivo(tipoSolicitacao) ? motivo : undefined,
              motivoSolicitacaoDetalhe: tipoSolicitacaoExigeMotivo(tipoSolicitacao) ? motivoDetalhe.trim() || null : undefined,
              // Vinculada automaticamente quando só há 1 alocação ativa, escolhida no
              // formulário quando há mais de 1 (ver alocacaoEscolhida acima), ou vinda do
              // prefill de Alocações (deep-link) — dá visibilidade ao Responsável Raro
              // dessa alocação específica em "Minha Equipe" (ver getByAlocacaoResponsavel).
              alocacaoReferenciadaId: alocacaoEscolhida?.id ?? undefined,
              alocacaoResponsavelId: alocacaoEscolhida?.responsibleId ?? undefined,
              alocacaoResponsavelNome: alocacaoEscolhida?.responsibleName ?? undefined,
              dadosCliente: dadosClienteParaSalvar,
              // 1 item por sistema marcado, mesma ação pra todos — SI marca "Concluído"
              // por item no checklist de Acessos (AcessosChecklist), mesmo controle já
              // usado em Admissão/Desligamento.
              acessos:
                isAcessos && acaoAcesso && sistemasAcesso.size > 0
                  ? Array.from(sistemasAcesso).map((sistema) => ({
                      sistema,
                      acao: acaoAcesso,
                      status: 'pendente' as const,
                    }))
                  : undefined,
            },
        // Formatação não gera nenhuma Movimentação (ver naoTemMovimentacao) — é só um
        // chamado pro SI, sem remessa/logística de equipamento associada.
        movimentacao: naoTemMovimentacao ? undefined : {
          tipoFluxo: TIPO_FLUXO_PADRAO_POR_SOLICITACAO[tipoSolicitacao],
          // Desligamento (sugereDevolucaoTotal): um item por equipamento hoje vinculado ao
          // colaborador — todos serão devolvidos, sem escolha (ver definitions). "Devolução"
          // (sugestaoNaMovimentacaoPrincipal) com 2+ equipamentos escolhidos usa o mesmo
          // formato (1 item por equipamento). Com 0 ou 1, segue o fluxo normal de 1 item via
          // formulário SO-first, enriquecido com equipamentoSugeridoId quando houver um
          // escolhido — Mudança de Projeto/Troca de Equipamento aplicam a sugestão na 2ª
          // Movimentação (devolução) em vez desta.
          equipamentosIntake: [
            ...(sugereDevolucaoTotal
              ? itensIntakePorEquipamentos(equipamentosColaborador)
              : sugestaoNaMovimentacaoPrincipal
                ? itensIntakePorEquipamentos(equipamentosParaDevolver)
                : mostrarCamposEquipamento
                  ? [
                      {
                        id: crypto.randomUUID(),
                        tipoEquipamento: tipoEquipamento || null,
                        sistemaOperacional: soPrincipal && soPrincipal !== 'sem_so' ? soPrincipal : null,
                        homologado: homologadoVisivel ? homologado : null,
                        // Cliente sem regra habilitada: equipamento é sempre Raro, nem
                        // pergunta (ver propriedadeVisivel).
                        propriedadeEquipamento: propriedadeVisivel ? propriedadeEquipamento || null : 'raro',
                        equipamentoSugeridoId: sugestaoNaMovimentacaoPrincipal
                          ? (equipamentosParaDevolver[0]?.id ?? null)
                          : null,
                        // Periféricos e Acessórios → Periférico/Outros: qual item (aparece no card
                        // como "Descrito pelo solicitante" e já preenche a descrição no vínculo).
                        ...(pedeDescricaoItem ? { descricaoLivre: descricaoItemPedido.trim() } : {}),
                      },
                    ]
                  : []),
            // Só entra aqui quando é a própria devolução (não mudança/troca, que tem
            // Movimentação separada — ver movimentacaoDevolucao abaixo).
            ...(sugestaoNaMovimentacaoPrincipal ? itemOutroEquipamentoDevolucao : []),
          ],
          // Projeto/cliente que este envio atende — o mesmo da Solicitação (ver
          // Movimentacao.clienteId sobre por que isso é por Movimentação, não só na
          // Solicitação: numa Troca de Projeto, a devolução abaixo pode ser de outro Cliente).
          clienteId: cliente?.id ?? null,
          dataPrevista: data ? (dataInputParaDate(data) as any) : null,
          // Alocação por Movimentação: o responsável dela vê esta Movimentação (a function
          // resolve nome/e-mail a partir da alocação — ver onMovimentacaoWriteAlocacao).
          alocacaoReferenciadaId: alocacaoEscolhida?.id ?? null,
        },
        movimentacaoDevolucao: geraDevolucaoAutomatica
          ? {
              tipoFluxo: 'devolucao',
              // Troca: mesmo projeto do envio. Mudança de Projeto: a alocação mais recente da
              // pessoa no cliente que ela está deixando (o gestor de lá vê a devolução).
              alocacaoReferenciadaId:
                tipoSolicitacao === 'troca_equipamento'
                  ? (alocacaoEscolhida?.id ?? null)
                  : (alocacoesColaborador
                      .filter((alocacao) => clienteDevolucao?.id && alocacao.clientId === clienteDevolucao.id)
                      .sort((a, b) => (b.startDate?.toMillis?.() ?? 0) - (a.startDate?.toMillis?.() ?? 0))[0]?.id ?? null),
              // Troca de Equipamento não muda de projeto (dano/upgrade) — usa direto o
              // mesmo Cliente do envio, sem perguntar de novo. Mudança de Projeto usa o
              // que foi informado (campo "Cliente" junto do equipamento a devolver,
              // opcional — sem preencher, os componentes caem no fallback `Solicitacao.clienteId`).
              clienteId: tipoSolicitacao === 'troca_equipamento' ? (cliente?.id ?? null) : (clienteDevolucao?.id ?? null),
              // 1 item por equipamento escolhido (pode ser mais de um, ex.: notebook +
              // monitor) + o item de texto livre, se preenchido. Sem nenhum dos dois,
              // fica 1 item vazio pro SI completar manualmente depois (mesmo padrão de
              // "nenhum equipamento ainda").
              equipamentosIntake:
                equipamentosParaDevolver.length > 0
                  ? [...itensIntakePorEquipamentos(equipamentosParaDevolver), ...itemOutroEquipamentoDevolucao]
                  : itemOutroEquipamentoDevolucao.length > 0
                    ? itemOutroEquipamentoDevolucao
                    : [
                        {
                          id: crypto.randomUUID(),
                          tipoEquipamento: null,
                          sistemaOperacional: null,
                          homologado: null,
                          propriedadeEquipamento: null,
                          equipamentoSugeridoId: null,
                        },
                      ],
              // Data própria da devolução (opcional) — pode não ser imediata, diferente da
              // data de envio acima (campo "Data prevista para recolhimento").
              dataPrevista: dataRecolhimentoDevolucao ? (dataInputParaDate(dataRecolhimentoDevolucao) as any) : null,
            }
          : undefined,
      });

      // Observações da abertura = 1ª postagem, assinada por quem abriu. Falhar aqui não
      // desfaz a Solicitação (já criada) — só avisa pra postar de novo pelo detalhe.
      if (observacoes.trim()) {
        try {
          await comentarioSolicitacaoService.create(
            solicitacaoId,
            observacoes,
            { id: user.collaboratorId, name: user.displayName ?? user.email, avatar: user.photoURL ?? null },
            user.email
          );
        } catch (error) {
          console.error('Erro ao enviar observação da abertura:', error);
          toast.warning('Solicitação criada, mas a observação não foi salva — poste de novo pela tela da solicitação.');
        }
      }

      toast.success('Solicitação criada com sucesso.');
      onCriada(solicitacaoId);
    } catch (error) {
      // Erro já é reportado via toast pelo próprio hook de mutation (onError).
      console.error('Erro ao criar solicitação:', error);
    }
  };

  // Mudança de Projeto/Troca de Equipamento: o que vai ser enviado importa mais na hora
  // de preencher (é o motivo da solicitação) do que o que vai ser devolvido — por isso
  // essa seção vem primeiro e ganha um nome que deixa claro que é sobre o envio, não sobre
  // o equipamento em geral. Nos demais tipos, mantém "Equipamento" e a ordem de sempre.
  const nomeSecaoEquipamentoEnvio = geraDevolucaoAutomatica ? 'Equipamento a ser enviado' : 'Equipamento';
  // Troca de Equipamento não muda de projeto (normalmente é dano/upgrade) — o equipamento
  // devolvido é sempre do mesmo Cliente do envio, sem precisar perguntar de novo. Só
  // Mudança de Projeto pode ter Clientes diferentes entre envio e devolução.
  const mostrarClienteNaDevolucao = tipoSolicitacao === 'mudanca_projeto';

  const secaoEquipamentoDevolver = buscaEquipamentoDevolucao && (
    <div className="rounded-md border border-gray-200 p-4">
      <p className="font-montserrat font-medium text-gray-900">Equipamento(s) a devolver</p>

      {geraDevolucaoAutomatica && (
        <p className="mt-1 text-sm text-gray-500">
          {tipoSolicitacao === 'mudanca_projeto' ? 'Mudança de Projeto' : 'Troca de Equipamento'} sempre gera
          também uma Movimentação de devolução do equipamento atual.
        </p>
      )}
      {sugereDevolucaoTotal && (
        <p className="mt-1 text-sm text-gray-500">
          Todos os equipamentos vinculados a este colaborador serão devolvidos.
        </p>
      )}

      {equipamentosColaborador.length === 0 && (
        <p className="mt-2 text-sm text-gray-500">
          Nenhum equipamento vinculado a este colaborador ainda — o SI busca manualmente depois.
        </p>
      )}

      {sugereDevolucaoTotal && equipamentosColaborador.length > 0 && (
        <ListaEquipamentosColaborador equipamentos={equipamentosColaborador} />
      )}

      {sugereDevolucaoUnica && equipamentosColaborador.length > 0 && (
        <div className="mt-2">
          <label className="block text-sm font-medium text-gray-700">
            Quais equipamentos serão devolvidos?{outroEquipamentoDevolucao.trim() ? '' : ' *'}
          </label>
          <p className="text-xs text-gray-500">Marque na lista ou, se não estiver nela, descreva abaixo.</p>
          <ListaEquipamentosColaborador
            equipamentos={equipamentosColaborador}
            selecionados={equipamentosDevolucaoEscolhidosIds}
            onToggle={(id) =>
              setEquipamentosDevolucaoEscolhidosIds((atual) => {
                const novo = new Set(atual);
                if (novo.has(id)) novo.delete(id);
                else novo.add(id);
                return novo;
              })
            }
          />
        </div>
      )}

      {sugereDevolucaoUnica && (
        <div className="mt-2">
          <label className="block text-sm font-medium text-gray-700">
            Outro equipamento a devolver que não está na lista acima?
          </label>
          <input
            type="text"
            className="input-primary mt-1 w-full"
            placeholder="Descreva o equipamento"
            value={outroEquipamentoDevolucao}
            onChange={(event) => setOutroEquipamentoDevolucao(event.target.value)}
          />
        </div>
      )}

      {geraDevolucaoAutomatica && (
        <div className="mt-3 space-y-3 border-t border-gray-100 pt-3">
          {mostrarClienteNaDevolucao && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Cliente</label>
              <ClientCombobox
                selectedId={clienteDevolucao?.id ?? null}
                placeholder="Buscar cliente..."
                onChange={(client) => setClienteDevolucao(client ? { id: client.id, name: client.name } : null)}
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700">Data prevista para recolhimento</label>
            <input
              type="date"
              className="input-primary mt-1 w-full"
              value={dataRecolhimentoDevolucao}
              onChange={(event) => setDataRecolhimentoDevolucao(event.target.value)}
            />
          </div>
        </div>
      )}
    </div>
  );

  const campoData = (
    <div>
      <label className="block text-sm font-medium text-gray-700">
        {ehDevolucao ? 'Data prevista para devolução' : 'Data'}
        {dataObrigatoria ? ' *' : ''}
      </label>
      {tipoSolicitacao === 'desligamento' && (
        <p className="mt-1 text-xs text-gray-500">
          Essa é a data do último dia de trabalho, a partir dela já podemos pedir a devolução do equipamento.
        </p>
      )}
      <input type="date" className="input-primary mt-1 w-full" value={data} onChange={(event) => setData(event.target.value)} />
    </div>
  );

  const secaoEquipamentoEnvio = (
    <div className="space-y-4 rounded-md border border-gray-200 p-4">
      <p className="font-montserrat font-medium text-gray-900">{nomeSecaoEquipamentoEnvio}</p>

      {geraDevolucaoAutomatica && (
        <div>
          <label className="block text-sm font-medium text-gray-700">Cliente *</label>
          <ClientCombobox
            selectedId={cliente?.id ?? null}
            onChange={(client) => setCliente(client ? { id: client.id, name: client.name } : null)}
          />
        </div>
      )}

      {mostrarCamposEquipamento}

      {mostrarCamposEquipamento && (
        <>
          {isPedidoPerifericos ? (
            <TipoEquipamentoPedido
              soPrincipal={soPrincipal}
              tipoSemSo={tipoSemSo}
              onChange={(valor) => {
                setSoPrincipal(valor.soPrincipal);
                setTipoSemSo(valor.tipoSemSo);
                setHomologado(false);
              }}
              descricao={descricaoItemPedido}
              onChangeDescricao={setDescricaoItemPedido}
            />
          ) : (
          <div>
            <label className="block text-sm font-medium text-gray-700">Qual Sistema Operacional? *</label>
            <select
              className="input-primary mt-1 w-full"
              value={soPrincipal}
              onChange={(event) => {
                setSoManual(true);
                setSoPrincipal(event.target.value as SistemaOperacionalPrincipal | 'sem_so');
                setTipoSemSo('');
                setHomologado(false);
              }}
            >
              <option value="">Selecione...</option>
              {Object.entries(SISTEMA_OPERACIONAL_PRINCIPAL_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
              <option value="sem_so">Não tem sistema operacional (monitor, periférico, outro)</option>
            </select>
            {sugestaoSo && (
              <p className="mt-1 text-xs text-gray-500">
                Preenchido com o do equipamento que vai ser devolvido — pode trocar.
              </p>
            )}
          </div>
          )}

          {tipoSemSoVisivel && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Tipo de Equipamento *</label>
              <select
                className="input-primary mt-1 w-full"
                value={tipoSemSo}
                onChange={(event) => {
                  // Sai da sugestão guardando o SO que estava valendo (sem_so).
                  setSoManual(true);
                  setSoPrincipal(soPrincipal);
                  setTipoSemSo(event.target.value as TipoEquipamentoMovimentacao);
                }}
              >
                <option value="">Selecione...</option>
                {TIPOS_EQUIPAMENTO_SEM_SO.map((value) => (
                  <option key={value} value={value}>
                    {TIPO_EQUIPAMENTO_LABELS[value]}
                  </option>
                ))}
              </select>
            </div>
          )}

          {homologadoVisivel && (
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                checked={homologado}
                onChange={(event) => setHomologado(event.target.checked)}
              />
              Equipamento homologado
            </label>
          )}

          {propriedadeVisivel && (
            <div>
              <p className="text-sm font-medium text-gray-700">Propriedade do Equipamento</p>
              <div className="mt-1 flex flex-wrap gap-3">
                {PROPRIEDADE_EQUIPAMENTO_OPCOES.map((value) => (
                  <label key={value} className="flex items-center gap-1.5 text-sm text-gray-700">
                    <input
                      type="radio"
                      name="propriedadeEquipamento"
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      checked={propriedadeEquipamento === value}
                      onChange={() => setPropriedadeEquipamento(value)}
                    />
                    {labelPropriedadeEquipamento(value, cliente?.name ?? null)}
                  </label>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {dataVisivel && campoData}

      {!mostrarCamposEquipamento && (
        <p className="text-sm text-gray-500">Os dados de equipamento serão definidos pelo SI depois.</p>
      )}
    </div>
  );

  return (
    <div className={className ?? 'space-y-6 p-6'}>
      <div>
        <h1 className="font-montserrat text-3xl font-bold text-gray-900">Nova Solicitação</h1>
        <p className="mt-1 text-sm text-gray-600">
          Admissão, desligamento, equipamentos e acessos gerenciados pelo SI.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="max-w-2xl space-y-6 rounded-lg border border-gray-200 bg-white p-6">
        <div>
          <label className="block text-sm font-medium text-gray-700">Tipo de solicitação *</label>
          <select
            className="input-primary mt-1 w-full"
            value={tipoSolicitacao}
            onChange={(event) => setTipoSolicitacao(event.target.value as TipoSolicitacao)}
          >
            <option value="" disabled>
              Selecione o tipo...
            </option>
            {opcoesTipoSolicitacao.map((opcao) => (
              <option key={opcao.value} value={opcao.value}>
                {opcao.label}
              </option>
            ))}
          </select>
        </div>

        {isAdmissao ? (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-4">
            <p className="text-sm font-medium text-amber-800">
              Pessoa ainda não cadastrada no Digital
            </p>
            <p className="mt-1 text-sm text-amber-700">
              Informe nome e e-mail do futuro colaborador.
            </p>

            <div className="mt-3">
              <label className="block text-sm font-medium text-gray-700">Nome *</label>
              <input
                type="text"
                className="input-primary mt-1 w-full"
                value={nomePendente}
                onChange={(event) => setNomePendente(event.target.value)}
                placeholder="Nome do futuro colaborador"
              />
            </div>

            <div className="mt-3">
              <label className="block text-sm font-medium text-gray-700">E-mail *</label>
              <input
                type="email"
                className="input-primary mt-1 w-full"
                value={emailPendente}
                onChange={(event) => setEmailPendente(event.target.value)}
                placeholder="e-mail informado para a admissão"
              />
            </div>

            <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700">Cargo</label>
                <PositionCombobox
                  selectedId={positionIdPendente}
                  onChange={(position) => setPositionIdPendente(position?.id ?? '')}
                  positions={positions}
                  loading={positionsLoading}
                  error={positionsError}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Senioridade</label>
                <SeniorityCombobox
                  selectedId={seniorityIdPendente}
                  onChange={(seniority) => setSeniorityIdPendente(seniority?.id ?? '')}
                  seniorities={seniorities}
                  loading={senioritiesLoading}
                  error={senioritiesError}
                />
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Desligamento: nunca é "pra mim" — ninguém abre o próprio desligamento
                (decisão de produto). Pula direto pra escolha do colaborador. */}
            {!isDesligamento && (
              <div>
                <p className="text-sm font-medium text-gray-700">Para quem é esta solicitação?</p>
                <div className="mt-1 flex flex-wrap gap-3">
                  <label className="flex items-center gap-1.5 text-sm text-gray-700">
                    <input
                      type="radio"
                      name="paraQuem"
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      checked={paraMim}
                      onChange={() => setParaMim(true)}
                    />
                    Para mim
                  </label>
                  <label className="flex items-center gap-1.5 text-sm text-gray-700">
                    <input
                      type="radio"
                      name="paraQuem"
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      checked={!paraMim}
                      onChange={() => setParaMim(false)}
                    />
                    Para outra pessoa
                  </label>
                </div>
              </div>
            )}

            {(!paraMim || isDesligamento) && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Colaborador *</label>
                <CollaboratorCombobox
                  selectedId={colaboradorSelecionado?.id ?? null}
                  onChange={setColaboradorSelecionado}
                  placeholder="Buscar colaborador..."
                />
              </div>
            )}
          </>
        )}

        {/* Só pergunta quando é ambíguo (2+ alocações ativas) — o colaborador pode estar
            em mais de um projeto ao mesmo tempo, e é essa alocação que decide qual gestor
            vê esta Solicitação em "Minha Equipe" (não o gestor geral do perfil dele). Com
            0 ou 1 alocação ativa não há nada a perguntar (ver alocacaoEscolhida), exceto na
            Nova Alocação, que sempre pergunta (a única ativa costuma ser a atual). 'acessos'
            nunca pergunta — não é amarrado a projeto/cliente (ver isAcessos). */}
        {!isAcessos && perguntaAlocacao && (
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {ehNovaAlocacao ? 'Para qual projeto é a nova alocação? *' : 'A qual projeto/alocação esta solicitação se refere? *'}
            </label>
            <select
              className="input-primary mt-1 w-full"
              value={alocacaoEscolhidaId}
              onChange={(event) => {
                setAlocacaoEscolhidaId(event.target.value);
                // Sai o Cliente que veio da alocação escolhida antes — escolhe no campo abaixo.
                if (event.target.value === ALOCACAO_OUTRO_CLIENTE) setCliente(null);
              }}
            >
              <option value="" disabled>
                Selecione a alocação...
              </option>
              {alocacoesAtivas.map((alocacao) => (
                <option key={alocacao.id} value={alocacao.id}>
                  {alocacao.clientName ?? 'Cliente não informado'}
                  {alocacao.projectName ? ` - ${alocacao.projectName}` : ''}
                </option>
              ))}
              {ehNovaAlocacao && (
                <option value={ALOCACAO_OUTRO_CLIENTE}>Outro cliente (alocação ainda não cadastrada)</option>
              )}
            </select>
          </div>
        )}

        {/* Formatação já vem com o Cliente definido pelo wizard de Alocações (o registro
            da alocação já sabe qual é) — não pede de novo aqui, só aparece depois no
            acompanhamento da Solicitação (ver SolicitacaoDetailsContent). Some também
            quando o Cliente já foi derivado da Alocação (ver useEffect acima) — só
            pergunta na mão quando não existe nenhuma Alocação ativa pra derivar. */}
        {!geraDevolucaoAutomatica &&
          !naoTemMovimentacao &&
          !alocacaoEscolhida?.clientId &&
          // Nova Alocação com alocações ativas: só depois de escolher "Outro cliente".
          (!ehNovaAlocacao || alocacoesAtivas.length === 0 || alocacaoEscolhidaId === ALOCACAO_OUTRO_CLIENTE) && (
          <div>
            <label className="block text-sm font-medium text-gray-700">Cliente *</label>
            <ClientCombobox
              selectedId={cliente?.id ?? null}
              onChange={(client) => setCliente(client ? { id: client.id, name: client.name } : null)}
            />
          </div>
        )}

        {isAcessos && (
          <>
            <div>
              <p className="text-sm font-medium text-gray-700">Ação *</p>
              <div className="mt-1 flex flex-wrap gap-3">
                {(Object.entries(ACAO_ACESSO_SOLICITACAO_LABELS) as Array<[AcaoAcesso, string]>).map(
                  ([valor, label]) => (
                    <label key={valor} className="flex items-center gap-1.5 text-sm text-gray-700">
                      <input
                        type="radio"
                        name="acaoAcesso"
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        checked={acaoAcesso === valor}
                        onChange={() => {
                          setAcaoAcesso(valor);
                          setSistemasAcesso(new Set());
                        }}
                      />
                      {label}
                    </label>
                  )
                )}
              </div>
            </div>

            {acaoAcesso && (
              <div>
                <p className="text-sm font-medium text-gray-700">Sistema * (1 ou mais)</p>
                <div className="mt-1 flex flex-wrap gap-3">
                  {sistemasAcessoDisponiveis.map((sistema) => (
                    <label key={sistema} className="flex items-center gap-1.5 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        checked={sistemasAcesso.has(sistema)}
                        onChange={() => toggleSistemaAcesso(sistema)}
                      />
                      {sistema}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {!naoTemMovimentacao &&
          (geraDevolucaoAutomatica ? (
            <>
              {secaoEquipamentoEnvio}
              {secaoEquipamentoDevolver}
            </>
          ) : (
            <>
              {secaoEquipamentoDevolver}
              {/* Devolução: sem campos de equipamento, a data vai junto do motivo (abaixo). */}
              {!ehDevolucao && secaoEquipamentoEnvio}
            </>
          ))}

        {tipoSolicitacao && tipoSolicitacaoExigeMotivo(tipoSolicitacao) && (
          <div className="space-y-2">
            <MotivoSolicitacaoCampos
              tipoSolicitacao={tipoSolicitacao}
              motivo={motivo}
              detalhe={motivoDetalhe}
              onChangeMotivo={setMotivo}
              onChangeDetalhe={setMotivoDetalhe}
            />
            {ehDevolucao && dataVisivel && campoData}
          </div>
        )}

        <DadosClienteAbertura
          campos={camposDadosCliente}
          clienteNome={cliente?.name ?? ''}
          valores={dadosCliente}
          onChange={setDadosCliente}
          sugestoes={{
            gestor_raro: alocacaoEscolhida?.responsibleName,
            gestor_cliente: alocacaoEscolhida?.clientResponsible,
          }}
        />

        <div>
          <label className="block text-sm font-medium text-gray-700">Observações</label>
          <p className="text-xs text-gray-500">
            Fica como a primeira postagem nas Observações da solicitação, com o seu nome.
          </p>
          <textarea
            className="input-primary mt-1 w-full"
            rows={4}
            value={observacoes}
            onChange={(event) => setObservacoes(event.target.value)}
          />
        </div>

        <p className="text-xs text-gray-500">* Campos obrigatórios</p>

        {camposFaltando.length > 0 && (
          <Alert
            variant="warning"
            title="Campos obrigatórios pendentes"
            description={`Preencha: ${camposFaltando.join(', ')}`}
          />
        )}

        <div className="flex justify-end gap-3">
          <button type="button" className="btn-secondary" onClick={onCancelar}>
            Cancelar
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={!podeSubmeter || criarSolicitacaoComMovimentacao.isPending}
          >
            {criarSolicitacaoComMovimentacao.isPending ? 'Criando...' : 'Criar Solicitação'}
          </button>
        </div>
      </form>
    </div>
  );
}
